import { useEffect, useState } from "react";
import type { PageViewDTO } from "../../shared/types";
import { PAGE_SANDBOX, PAGE_VISIBILITIES } from "../../shared/types";
import { PageVisibilityBadge } from "../components/PageVisibility";
import { ShareDialog } from "../components/ShareDialog";
import { TextLink } from "../components/TextLink";
import { Button, timeAgo } from "../components/ui";
import { ApiError, get } from "../lib/api";

/**
 * A hosted page (/p/<id>): our own frame around it — the title, who wrote it and for whom, who may see it, the
 * owner's Share — and the page's content in a sandboxed frame from the pages host, which has none of our cookies and
 * can act on nothing of the platform (security plan D1). Signed out it opens a public page; a private one asks to sign in.
 */
export function PageViewPage({ pageId }: { pageId: string }) {
  const [view, setView] = useState<PageViewDTO | null>(null);
  const [missing, setMissing] = useState(false);
  const [sharing, setSharing] = useState(false);

  const load = () =>
    get<PageViewDTO>(`/api/pages/${encodeURIComponent(pageId)}/view`).then(
      (v) => {
        setView(v);
        document.title = v.page.title;
      },
      (err) => err instanceof ApiError && err.status !== 401 && setMissing(true),
    );
  useEffect(() => void load(), [pageId]);

  if (missing)
    return (
      <div className="grid min-h-dvh place-items-center bg-canvas px-4">
        <div className="card max-w-sm p-8 text-center">
          <img src="/favicon.png" alt="Pandito" className="mx-auto mb-3 size-14 rounded-2xl shadow-sm ring-1 ring-line" />
          <h1 className="text-heading font-semibold tracking-tight">This page is not available</h1>
          <p className="mt-1 text-sm text-muted">It may not be shared with you, or it was deleted.</p>
          <TextLink to="/" className="mt-4 inline-block text-sm">Go to AgentOS</TextLink>
        </div>
      </div>
    );
  if (!view) return <div className="grid h-dvh place-items-center text-sm text-faint">Loading…</div>;

  const { page } = view;
  return (
    <div className="flex h-dvh flex-col bg-canvas text-fg">
      <header className="glass z-20 border-b border-line">
        <div className="flex items-center gap-3 px-4 py-2.5">
          <a href="/" className="shrink-0" aria-label="AgentOS">
            <img src="/favicon-32.png" alt="" className="size-8 rounded-lg shadow-sm ring-1 ring-line" />
          </a>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-heading font-semibold tracking-tight">{page.title}</h1>
            <div className="truncate text-xs text-muted">
              {page.createdByAgentSlug ? `by ${page.createdByAgentSlug}` : "a page"}
              {page.ownerName ? ` for ${page.ownerName}` : ""} · updated {timeAgo(page.updatedAt)}
            </div>
          </div>
          <PageVisibilityBadge visibility={page.visibility} />
          {view.canShare && <Button size="sm" onClick={() => setSharing(true)}>Share</Button>}
        </div>
      </header>
      {view.frameUrl ? (
        <iframe
          src={view.frameUrl}
          title={page.title}
          sandbox={PAGE_SANDBOX}
          referrerPolicy="no-referrer"
          className="min-h-0 w-full flex-1 border-0 bg-white"
        />
      ) : (
        <div className="grid flex-1 place-items-center p-8 text-sm text-muted">Pages are not set up on this installation (no pages host).</div>
      )}
      {view.canShare && (
        <ShareDialog
          url={`/api/pages/${page.id}/sharing`}
          title="Share this page"
          visibilities={PAGE_VISIBILITIES}
          open={sharing}
          onClose={() => setSharing(false)}
          onSaved={() => {
            setSharing(false);
            void load();
          }}
        />
      )}
    </div>
  );
}
